import { useState } from 'react';
import * as React from 'react';
import { useTypes, useCreateType, useUpdateType, useDeleteType, useChildTypeType, useGetFilterType } from '@/hooks/useLocalStorage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Edit, Trash2, ChevronDown, ChevronRight, Folder, MoreVertical, Copy, Move, Filter, List, TreePine, Loader2 } from 'lucide-react';
import { insertTypeSchema, InsertType, Type } from '../shared/schema';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PaginationControl } from '@/widgets/renderPagination';
import { DeleteConfirmationDialog } from '@/widgets/DeleteModal';
import { parseApiErrorMessage } from '@/hooks/parseApiErrorMessage';
import { AlertDialog } from '@/widgets/AlertDailog';
import { FilterTypeSearch } from '@/widgets/searchFilterType';

function CreateTypeDialog() {
    const [open, setOpen] = useState(false);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);

    const createTypeMutation = useCreateType();

    const form = useForm<InsertType>({
        resolver: zodResolver(insertTypeSchema),
        defaultValues: {
            key: '',
            name: '',
            description: '',
            seq: 0,
            useFlag: true,
            // ensure newly created types are top-level
            parentType: 'TYPES'
        }
    });

    const onSubmit = async (data: InsertType) => {
        try {
            await createTypeMutation.mutateAsync(data);
            setOpen(false);
            form.reset();
        } catch (error) {
            console.error('Error creating type:', error);
        }
    };

    React.useEffect(() => {
        // console.log("updatetypMutation", updateTypeMutation)
        if (createTypeMutation.isSuccess === true) {
            setOpen(false);
            setReqStatus(createTypeMutation.isSuccess)
            setAlertMessage(`Type Added Successfully!`);
            setAlertOpen(true);
            createTypeMutation.reset();
        } else if (createTypeMutation.isError) {
            const raw = createTypeMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(createTypeMutation.isSuccess)
            setOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            createTypeMutation.reset();
        }
    }, [createTypeMutation])

    return (
        <div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    <Button className="bg-workfinity-500 hover:bg-workfinity-600">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Type
                    </Button>
                </DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create New Type</DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Customer Types" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Classification of customer entities" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="seq"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Sequence</FormLabel>
                                        <FormControl>
                                            <Input type="number" placeholder="0" {...field} onChange={(e) => field.onChange(parseInt(e.target.value) || 0)} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="flex justify-end space-x-2">
                                <Button type="button" variant="outline" disabled={createTypeMutation.isPending} onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={createTypeMutation.isPending}>
                                    {createTypeMutation.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Creating......
                                        </>
                                    ) : (
                                        "Create Type"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </div>

    );
}

// Add Child Type Dialog Component
function AddChildTypeDialog({ parentType, trigger, onAlert }: {
    parentType: Type;
    trigger: React.ReactNode;
    onAlert: (msg: string, ok: boolean) => void
}) {
    const [open, setOpen] = useState(false);

    const createTypeMutation = useCreateType();
    console.log('typeItem', parentType)
    const form = useForm<InsertType>({
        resolver: zodResolver(insertTypeSchema),
        defaultValues: {
            key: '',
            name: '',
            description: '',
            seq: 0,
            useFlag: true,
            parentType: parentType.name,
            parentCode: parentType.code
        }
    });

    React.useEffect(() => {
        form.reset({
            key: '',
            name: '',
            description: '',
            seq: 0,
            useFlag: true,
            parentType: parentType.name,
            parentCode: parentType.code
        });
    }, [parentType, form]);

    const onSubmit = async (data: InsertType) => {
        try {
            await createTypeMutation.mutateAsync(data);
            setOpen(false);
            form.reset();
        } catch (error) {
            console.error('Error creating child type:', error);
        }
    };

    React.useEffect(() => {
        if (createTypeMutation.isSuccess === true) {
            setOpen(false);
            onAlert("Child Type Added Successfully!", true);
            createTypeMutation.reset();
        } else if (createTypeMutation.isError) {
            const raw = createTypeMutation.error as Error;
            setOpen(false);
            onAlert(parseApiErrorMessage(raw.message), false);
            createTypeMutation.reset();
        }
    }, [createTypeMutation])

    return (
        <div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>{trigger}</DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Child Type to {parentType.name}</DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter description" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="flex justify-end space-x-2">
                                <Button type="button" variant="outline" disabled={createTypeMutation.isPending} onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={createTypeMutation.isPending} className="bg-workfinity-500 hover:bg-workfinity-600">
                                    {createTypeMutation.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Adding Child Type...
                                        </>
                                    ) : (
                                        "Add Child Type"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Edit Type Dialog Component
function EditTypeDialog({ type, trigger, onAlert }: {
    type: Type;
    trigger: React.ReactNode;
    onAlert: (msg: string, ok: boolean) => void
}) {
    const [open, setOpen] = useState(false);

    const updateTypeMutation = useUpdateType();

    const form = useForm<InsertType>({
        resolver: zodResolver(insertTypeSchema),
        defaultValues: {
            key: type ? type.type : "",
            name: type ? type.name : "",
            description: type ? type.description || "" : "",
            parentType: type ? type.parentType || "" : "",
            parentCode: type ? type.parentCode || "" : "",
            code: type ? type.code || "" : "",
        }
    });

    React.useEffect(() => {
        form.reset({
            key: type ? type.type : "",
            name: type ? type.name : "",
            description: type ? type.description || "" : "",
            parentType: type ? type.parentType || "" : "",
            parentCode: type ? type.parentCode || "" : "",
            code: type ? type.code || "" : "",
        });
    }, [type, form]);

    const onSubmit = async (data: InsertType) => {
        console.log("data", data)
        try {
            await updateTypeMutation.mutateAsync({ key: type.code!, updates: data });
            setOpen(false);
        } catch (error) {
            console.error('Error updating type:', error);
        }
    };

    React.useEffect(() => {
        if (updateTypeMutation.isSuccess === true) {
            setOpen(false);
            onAlert("Type Edited successfully!", true);
            updateTypeMutation.reset();
        } else if (updateTypeMutation.isError) {
            const raw = updateTypeMutation.error as Error;
            onAlert(parseApiErrorMessage(raw.message), false);
            setOpen(false);
            updateTypeMutation.reset();
        }
    }, [updateTypeMutation])

    return (
        <div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>{trigger}</DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Type: {type.name}</DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit, (errors) => { console.log("FORM VALIDATION ERRORS", errors); })} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter description" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="seq"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Sequence</FormLabel>
                                        <FormControl>
                                            <Input type="number" placeholder="0" {...field} onChange={(e) => field.onChange(parseInt(e.target.value) || 0)} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="flex justify-end space-x-2">
                                <Button type="button" variant="outline" disabled={updateTypeMutation.isPending} onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={updateTypeMutation.isPending} className="bg-workfinity-500 hover:bg-workfinity-600">
                                    {updateTypeMutation.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Updating Type...
                                        </>
                                    ) : (
                                        "Update Type"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function TypeTableHierarchicalNode({
    topLevelTypesData,
    updateTypeMutation,
    pageIndex,
    pageSize,
    totalCount,
    onPageChange,
    onAlert
}: {
    topLevelTypesData: Type[];
    updateTypeMutation: any;
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (code: number) => void;
    onAlert: (msg: string, ok: boolean) => void
}) {
    const [currentData, setCurrentData] = useState<Type[]>([]);
    const [breadcrumbPath, setBreadcrumbPath] = useState<Type[]>([]);
    const [loading, setLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Type | null>(null);

    // const [alertOpen, setAlertOpen] = React.useState(false);
    // const [alertMessage, setAlertMessage] = React.useState("");
    // const [reqStatus, setReqStatus] = React.useState(false);

    const [childLoading, setChildLoading] = useState(false);
    const [parentType, setParentType] = useState("");
    const [parentCode, setParentCode] = useState("");

    const [showPopup, setShowPopup] = React.useState(false);
    const [dataHead, setDataHead] = React.useState<Type | undefined>();
    const [popupName, setPopupName] = React.useState("");
    const [dataContent, setDataContent] = React.useState<string | undefined>("");

    const deleteTypeMutation = useDeleteType();
    const { data: child, isLoading } = useChildTypeType(
        parentType,
        parentCode,
    );

    React.useEffect(() => {
        if (topLevelTypesData) {
            setCurrentData(topLevelTypesData);
        }
    }, [topLevelTypesData]);

    async function handleView(item: Type) {
        if (!item.hasChildTypes) return;
        const validChildren = child?.filter(c => c.parentCode === item.code)
        if (validChildren && validChildren.length > 0) {
            setCurrentData([...validChildren]);
            setBreadcrumbPath((prev) => [...prev, item]);
        } else {
            setChildLoading(true);
            setParentType(item.name)
            setParentCode(item.code ?? "")
            setBreadcrumbPath((prev) => [...prev, item]);
        }
    }

    React.useEffect(() => {
        if (!isLoading) {
            if (child && child?.length > 0) {
                setCurrentData(child)
                setChildLoading(false);
            } else {
                setChildLoading(false);
            }
        }
    }, [child])

    function handleBreadcrumbClick(index: number, item: Type) {
        const newPath = breadcrumbPath.slice(0, index + 1);
        setBreadcrumbPath(newPath);

        const lastItem = newPath[newPath.length - 1];
        if (!lastItem) {
            setCurrentData(topLevelTypesData);
            return;
        } else {
            setParentType(item.name)
            setParentCode(item.code ?? "")
        }
    }

    function handleDeleteClick(e: React.MouseEvent, item: Type) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;
        deleteTypeMutation.mutate({ key: itemToDelete.code! });
    }

    React.useEffect(() => {
        if (deleteTypeMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            onAlert("Type Deleted successfully!", true);
            deleteTypeMutation.reset();
        } else if (deleteTypeMutation.isError) {
            const raw = deleteTypeMutation.error as Error;
            onAlert(parseApiErrorMessage(raw.message), false);
            setDeleteDialogOpen(false);
            deleteTypeMutation.reset();
        }
    }, [deleteTypeMutation])

    return (
        <div className="grid grid-cols-[auto_1fr] gap-4">
            {/* Breadcrumb Sidebar */}
            <div className="border bg-white border-gray-200 rounded-lg p-4">
                <div className="flex flex-col space-y-2">
                    <Button
                        variant={breadcrumbPath.length === 0 ? "secondary" : "outline"}
                        size="sm"
                        className="w-7 h-7 md:w-14 md:h-8 p-0"
                        onClick={() => {
                            setBreadcrumbPath([]);
                            setCurrentData(topLevelTypesData);
                        }}
                    >
                        Types
                    </Button>

                    {breadcrumbPath.map((item, index) => (
                        <div key={item.code} style={{ marginLeft: `${(index + 1) * 16}px` }} className={`relative pl-6`}>
                            <span className="absolute left-0 top-1/2 w-4 h-0.5 bg-gray-400"></span>
                            <span className="absolute left-0 top-0 h-1/2 w-0.5 bg-gray-400"></span>
                            <Button
                                variant={index === breadcrumbPath.length - 1 ? "secondary" : "outline"}
                                size="sm"
                                onClick={() => handleBreadcrumbClick(index, item)}
                            >
                                {item.name}
                            </Button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Table */}
            <div className="border bg-white border-gray-200 rounded-lg p-2 w-full">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>View</TableHead>
                            <TableHead>ID</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Created At</TableHead>
                            <TableHead>Items</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6}>Loading...</TableCell>
                            </TableRow>
                        ) : (
                            currentData.map((type) => (
                                <TableRow key={type.code}>
                                    <TableCell>
                                        {type?.hasChildTypes ?
                                            <Button
                                                size="sm"
                                                variant="link"
                                                onClick={() => handleView(type)}
                                            >
                                                View
                                            </Button> :
                                            <span className="pl-3">{type?.type === "TYPES" && "No Item"}</span>
                                        }
                                    </TableCell>
                                    <TableCell>{type.code}</TableCell>
                                    <TableCell>{type.name}</TableCell>
                                    <TableCell className="truncate">
                                        {/* {type.description} */}
                                        <Button
                                            variant={"link"}
                                            onClick={() => {
                                                setPopupName("Type Description")
                                                setDataHead(type);
                                                setDataContent(type.description);
                                                setShowPopup(true);
                                            }}
                                        >
                                            View
                                        </Button>
                                    </TableCell>
                                    <TableCell>{new Date(type.createdAt).toLocaleString()}</TableCell>
                                    <TableCell>{type?.childTypesCount}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center space-x-2">
                                            <AddChildTypeDialog
                                                parentType={type}
                                                trigger={
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="w-7 h-7 md:w-8 md:h-8 p-0"
                                                        onClick={(e) => e.stopPropagation()}
                                                        tooltip={"Add this Type"}
                                                    >
                                                        <Plus className="h-3 w-3 md:h-4 md:w-4" />
                                                    </Button>
                                                }
                                                onAlert={onAlert}
                                            />
                                            <EditTypeDialog
                                                type={type}
                                                trigger={
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={(e) => e.stopPropagation()}
                                                        tooltip={"Edit this Type"}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                }
                                                onAlert={onAlert}
                                            />
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={(e) => handleDeleteClick(e, type)}
                                                className="text-red-600"
                                                tooltip={"Delete this Type"}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                {
                    breadcrumbPath.length === 0 &&
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                    />
                }
            </div>

            {/* Delete Confirmation */}
            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={deleteTypeMutation.isPending}
                itemName={itemToDelete?.name}
                itemType="type"
            />
            {/* <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            /> */}
            {showPopup && (
                <Popup
                    popupName={popupName}
                    heading={dataHead}
                    content={dataContent}
                    onClose={() => setShowPopup(false)}
                />
            )}
        </div>
    );
}


// Component to render type hierarchy nodes for Type Manager
function TypeHierarchyNode({
    type,
    level = 0,
    allTypes,
    updateTypeMutation,
    onAlert
}: {
    type: Type;
    level?: number;
    allTypes: Type[];
    updateTypeMutation: any;
    onAlert: (msg: string, ok: boolean) => void
}) {
    const [expanded, setExpanded] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Type | null>(null);

    const [children, setChildren] = useState<Type[]>([]);
    const [loading, setLoading] = useState(false);
    const [parentType, setParentType] = useState("");
    const [parentCode, setParentCode] = useState("");

    const deleteTypeMutation = useDeleteType();
    const { data: child, isLoading } = useChildTypeType(
        parentType,
        parentCode,
    );

    const handleToggleExpand = (type: Type) => {
        if (!expanded) {
            if (children.length === 0 && type?.hasChildTypes) {
                setLoading(true);
                setParentType(type.name)
                setParentCode(type.code ?? "")
            } else {
                setExpanded(true);
            }
        } else {
            setExpanded(false);
        }
    };

    React.useEffect(() => {
        if (!isLoading) {
            if (child && child?.length > 0) {
                setChildren(child)
                setLoading(false);
                setExpanded(true)
            } else {
                setLoading(false);
                setExpanded(false)
            }
        }
    }, [child])

    function handleDeleteClick(e: React.MouseEvent, item: Type) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;
        deleteTypeMutation.mutate({ key: type.code! });
    }

    React.useEffect(() => {
        if (deleteTypeMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            onAlert("Type Deleted successfully!", true);
            deleteTypeMutation.reset();
        } else if (deleteTypeMutation.isError) {
            const raw = deleteTypeMutation.error as Error;
            onAlert(parseApiErrorMessage(raw.message), false);
            setDeleteDialogOpen(false);
            deleteTypeMutation.reset();
        }
    }, [deleteTypeMutation])

    return (
        <div className={`${level > 0 ? 'ml-6 border-l border-gray-200 pl-4' : ''} bg-white`}>
            <div
                className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer group"
            // onClick={() => type?.hasChildTypes && handleToggleExpand(type)}
            >
                <div className="flex items-center space-x-2 md:space-x-3 min-w-0 flex-1">
                    {type?.hasChildTypes && (
                        <button
                            className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(type);
                            }}
                        >
                            {loading ? (
                                <span className="h-4 w-4 animate-spin">⏳</span>
                            ) : expanded ? (
                                <ChevronDown className="h-4 w-4" />
                            ) : (
                                <ChevronRight className="h-4 w-4" />
                            )}
                        </button>
                    )}
                    {!type?.hasChildTypes && <div className="w-6 flex-shrink-0" />}

                    <Folder className="h-4 w-4 md:h-5 md:w-5 text-amber-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0">
                            <div className="min-w-0">
                                <span className="font-medium text-gray-900 text-sm md:text-base truncate block">{type.name}</span>
                                <div className="text-xs text-gray-500 font-mono">{type.parentType}</div>
                            </div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">
                                {type.description}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    <AddChildTypeDialog
                        parentType={type}
                        trigger={
                            <Button size="sm" variant="ghost" className="w-7 h-7 md:w-8 md:h-8 p-0" tooltip={"Add child Type"} onClick={(e) => e.stopPropagation()}>
                                <Plus className="h-3 w-3 md:h-4 md:w-4" />
                            </Button>
                        }
                        onAlert={onAlert}
                    />

                    <EditTypeDialog
                        type={type}
                        trigger={
                            <Button size="sm" variant="ghost" className="w-8 h-8 p-0" tooltip={"Edit this Type"} onClick={(e) => e.stopPropagation()}>
                                <Edit className="h-4 w-4" />
                            </Button>
                        }
                        onAlert={onAlert}
                    />
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleDeleteClick(e, type)}
                        className="text-red-600"
                        tooltip={"Delete this Type"}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>

                </div>
            </div>

            {expanded && children.length > 0 && (
                <div className="mt-2 space-y-1">
                    {children.map((childType) => (
                        <TypeHierarchyNode
                            key={childType.key}
                            type={childType}
                            level={level + 1}
                            allTypes={allTypes}
                            updateTypeMutation={updateTypeMutation}
                            onAlert={onAlert}
                        />
                    ))}
                </div>
            )}
            {itemToDelete &&
                <DeleteConfirmationDialog
                    open={deleteDialogOpen}
                    onOpenChange={setDeleteDialogOpen}
                    onConfirm={handleConfirmDelete}
                    isPending={deleteTypeMutation.isPending}
                    itemName={(itemToDelete as Type).name}
                    itemType={"type"}
                />
            }

        </div>
    );
}

type FilterType = {
    key: string;
    name: string;
    [key: string]: any;
};


interface PopupProps {
    popupName: string;
    heading: Type | undefined;
    content: string | undefined;
    onClose: () => void;
}

function Popup({ popupName, heading, content, onClose }: PopupProps) {

    const isCode =
        typeof content === "string" &&
        (content.includes("\\n") || content.includes("{") || content.includes("}"));

    const formattedContent = isCode ? content.replace(/\\r?\\n/g, "\n") : content;

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{popupName}</DialogTitle>
                </DialogHeader>

                <div className="mt-4 space-y-4 text-sm text-gray-800">
                    <div>
                        <label className="font-semibold text-black">Name :</label>
                        <div className="border border-gray-400 rounded-md p-2 mt-1 bg-white">
                            {heading?.name || ""}
                        </div>
                    </div>

                    <div>
                        <label className="font-semibold text-black">
                            {popupName}
                        </label>
                        <div className="min-h-[200px] border border-gray-400 rounded-md p-2 mt-1 bg-white whitespace-pre-wrap font-mono text-sm">
                            {formattedContent || ""}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function TypeManager() {
    const [viewMode, setViewMode] = useState<"table" | "tree">("tree");
    const [topLevelTypes, setTopLevelTypes] = useState<(Type)[]>([]);

    const [typeInput, setTypeInput] = useState<string>("ALL");
    const [typeFilter, setTypeFilter] = useState<string>("ALL");
    const [filterOpen, setFilterOpen] = useState(false);

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);

    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(10);

    const [filterName, setFilterName] = useState<string>("");
    const [filterType, setFilterType] = useState<string>("");
    const [filterTypeData, setFilterTypeData] = useState<FilterType[]>([]);

    const { data: types, isLoading } = useTypes(
        pageIndex,
        pageSize,
        typeFilter === "ALL" ? undefined : typeFilter
    );

    const { data: filterTypes, isFetching: isFilterFetching } = useGetFilterType(
        filterName.length >= 3 ? filterName : undefined,
        filterType === "" ? undefined : filterType,
    );
    // console.log("filtertypes", filterTypeData)
    React.useEffect(() => {
        if (filterTypes && filterTypes.length > 0) {
            setFilterTypeData(filterTypes)
        } else {
            setFilterTypeData([])
        }
    }, [filterTypes])

    const updateTypeMutation = useUpdateType();
    const deleteTypeMutation = useDeleteType();

    React.useEffect(() => {
        setTopLevelTypes(types?.items ?? [])
        setTotalCount(types?.total ?? 0)

    }, [types])

    // Check for navigation from search
    React.useEffect(() => {
        const selectedItemStr = localStorage.getItem('cm:selectedItem');
        if (selectedItemStr) {
            try {
                const selectedItem = JSON.parse(selectedItemStr);
                console.log('Navigate to item in TypeManager:', selectedItem);
                // Clear the selection after handling
                localStorage.removeItem('cm:selectedItem');

                // TODO: Expand tree to show the selected type
                // This could involve expanding parent nodes and highlighting the target
            } catch (error) {
                console.error('Error parsing selected item:', error);
                localStorage.removeItem('cm:selectedItem');
            }
        }
    }, []);

    const handleSearch = () => {
        setTypeFilter(typeInput);
    };

    const handleRefresh = (item: boolean) => {
        setTypeInput("ALL");
        setTypeFilter("ALL");
        if (item) setFilterOpen(false)
    };

    const showAlert = (message: string, status: boolean) => {
        setAlertMessage(message);
        setReqStatus(status);
        setAlertOpen(true);
    };

    return (
        <div className="bg-gray-50 min-h-full">
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-xl md:text-2xl font-semibold text-gray-900">Type Manager</h2>
                        <p className="text-gray-600 mt-1 text-sm md:text-base">Manage types and their relationships</p>
                    </div>
                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                            <Button
                                size="sm"
                                variant={viewMode === "table" ? "default" : "ghost"}
                                onClick={() => setViewMode("table")}
                                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
                            >
                                <List className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                                <span className="hidden sm:inline">Table</span>
                                <span className="sm:hidden">Table</span>
                            </Button>
                            <Button
                                size="sm"
                                variant={viewMode === "tree" ? "default" : "ghost"}
                                onClick={() => setViewMode("tree")}
                                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
                            >
                                <TreePine className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                                <span className="hidden sm:inline">Tree</span>
                                <span className="sm:hidden">Tree</span>
                            </Button>
                        </div>
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={() => setFilterOpen(true)}
                        >
                            Filter Type
                        </Button>
                        <CreateTypeDialog />
                    </div>
                </div>
            </div>

            <div>
                {filterOpen &&
                    <Card className="mb-4 md:mb-6">
                        <CardContent className="pt-0">
                            <FilterTypeSearch
                                filterTypeData={filterTypeData}
                                isLoading={isFilterFetching}
                                value={typeInput}
                                onChange={setTypeInput}
                                onSearch={(query) => setFilterName(query)}
                            />


                            <div className="mt-4 flex justify-center space-x-2">
                                <Button onClick={handleSearch}>Search</Button>
                                <Button variant="outline" onClick={() => handleRefresh(false)}>
                                    Refresh
                                </Button>
                                <Button variant="outline" onClick={() => handleRefresh(true)}>
                                    Close
                                </Button>
                            </div>

                        </CardContent>
                    </Card>
                }
            </div>

            <Tabs defaultValue="types" className="space-y-6">

                <TabsContent value="types">
                    {!isLoading ?
                        viewMode === "table" ? (
                            <Card>
                                <CardContent className="p-4 md:p-6">
                                    <div className="space-y-2">
                                        {topLevelTypes && topLevelTypes.length > 0 ? (
                                            <>
                                                <TypeTableHierarchicalNode
                                                    topLevelTypesData={topLevelTypes}
                                                    updateTypeMutation={updateTypeMutation}
                                                    pageIndex={pageIndex}
                                                    pageSize={pageSize}
                                                    totalCount={totalCount}
                                                    onPageChange={setPageIndex}
                                                    onAlert={showAlert}
                                                />
                                            </>

                                        ) : (
                                            <div className="text-center py-8 text-gray-500">
                                                <p>
                                                    No types with codes found. Create types and codes to get
                                                    started.
                                                </p>
                                            </div>
                                        )}
                                        <AlertDialog
                                            open={alertOpen}
                                            onOpenChange={setAlertOpen}
                                            message={alertMessage}
                                            status={reqStatus}
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card>
                                <CardContent>
                                    <div className=" space-y-2">

                                        {topLevelTypes && topLevelTypes.length > 0 ? (
                                            topLevelTypes.map((type) => (
                                                <TypeHierarchyNode
                                                    key={type.key}
                                                    type={type}
                                                    level={0}
                                                    allTypes={topLevelTypes || []}
                                                    updateTypeMutation={updateTypeMutation}
                                                    onAlert={showAlert}
                                                />
                                            ))

                                        ) : (
                                            <div className="text-center py-8 text-gray-500">
                                                No top-level types found. Create a type to get started.
                                            </div>
                                        )}
                                        <PaginationControl
                                            pageIndex={pageIndex}
                                            pageSize={pageSize}
                                            totalCount={totalCount}
                                            onPageChange={setPageIndex}
                                        />
                                        <AlertDialog
                                            open={alertOpen}
                                            onOpenChange={setAlertOpen}
                                            message={alertMessage}
                                            status={reqStatus}
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        )
                        : <div className="p-6">Loading...</div>}
                </TabsContent>


            </Tabs>
        </div>
    );
}
